// src/Components/Emergency.js
import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Divider,
  Paper,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Warning as WarningIcon,
  Phone as PhoneIcon,
  LocalHospital as HospitalIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  EmergencyShare as EmergencyIcon,
  Close as CloseIcon,
  OpenInNew as MapIcon,
  Directions as DirectionsIcon,
  NotificationImportant as SOSIcon,
} from '@mui/icons-material';
import { getFirestore, collection, query, orderBy, onSnapshot, addDoc, serverTimestamp, doc, getDoc, getDocs, updateDoc, deleteDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../contexts/ProfileContext';
import { sendEmergencySOS } from '../services/emergencySOSService';
import LocationPicker, { buildMapsUrl, buildDirectionsUrl } from './LocationPicker';
import PrivacyControl from './PrivacyControl';
import TagMembers from './TagMembers';
import { notifyTaggedMembers } from '../services/notificationService';
import logger from '../utils/logger';

const Emergency = ({ familyBoardId }) => {
  const { currentUser } = useAuth();
  const { profile } = useProfile();
  const [emergencyInfo, setEmergencyInfo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedInfo, setSelectedInfo] = useState(null);
  const [editingInfo, setEditingInfo] = useState(null);
  const [formData, setFormData] = useState({
    category: 'emergency',
    name: '',
    phone: '',
    notes: '',
    location: null, // { name, address, lat, lng }
    privacy: { visibility: 'public', memberIds: [], groupTypes: [] },
    taggedMembers: []
  });
  const [sosDialogOpen, setSosDialogOpen] = useState(false);
  const [sosSending, setSosSending] = useState(false);
  const [sosSent, setSosSent] = useState(false);
  const [sosRecipients, setSosRecipients] = useState([]);
  const [sosRecipientsLoading, setSosRecipientsLoading] = useState(false);
  const db = getFirestore();

  const categories = [
    { value: 'emergency', label: 'Emergency Service', icon: <WarningIcon />, color: '#c62828' },
    { value: 'medical', label: 'Medical Contact', icon: <HospitalIcon />, color: '#0288d1' },
    { value: 'family', label: 'Family Contact', icon: <PersonIcon />, color: '#2e7d32' },
    { value: 'location', label: 'Important Location', icon: <LocationIcon />, color: '#f57c00' }
  ];

  useEffect(() => {
    if (!familyBoardId) return;

    const q = query(
      collection(db, 'families', familyBoardId, 'emergencyInfo'),
      orderBy('category', 'asc')
    );

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const infoData = [];
        querySnapshot.forEach((doc) => {
          infoData.push({ id: doc.id, ...doc.data() });
        });
        setEmergencyInfo(infoData);
        setLoading(false);
      },
      (error) => {
        logger.error('Error fetching emergency info:', error);
        setError('Failed to load emergency information');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [db, familyBoardId]);

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError('Name/title is required');
      return;
    }

    // Validate privacy settings
    if (formData.privacy.visibility === 'private' &&
        formData.privacy.memberIds.length === 0 &&
        formData.privacy.groupTypes.length === 0) {
      setError('Please select at least one member or group for private emergency information');
      return;
    }

    try {
      const infoData = {
        category: formData.category,
        name: formData.name,
        phone: formData.phone,
        notes: formData.notes,
        location: formData.location || null,
        // Backwards compat: keep top-level address/lat/lng
        address: formData.location?.address || formData.location?.name || '',
        lat: formData.location?.lat || null,
        lng: formData.location?.lng || null,
        privacy: formData.privacy,
        taggedMembers: formData.taggedMembers || [],
        authorId: currentUser.uid,
        authorName: currentUser.displayName || profile?.displayName || 'Anonymous',
        createdAt: serverTimestamp()
      };

      let infoId = editingInfo?.id;
      if (editingInfo) {
        await updateDoc(doc(db, 'families', familyBoardId, 'emergencyInfo', editingInfo.id), {
          ...infoData,
          updatedAt: serverTimestamp()
        });
        setSuccess('Emergency info updated');

        // Send notifications to newly tagged members when editing
        const previousTaggedMembers = editingInfo.taggedMembers || [];
        const newTaggedMembers = (formData.taggedMembers || []).filter(
          memberId => !previousTaggedMembers.includes(memberId)
        );

        if (newTaggedMembers.length > 0) {
          await notifyTaggedMembers(familyBoardId, newTaggedMembers, {
            type: 'emergency_info',
            id: editingInfo.id,
            title: formData.name,
            createdBy: currentUser.uid,
            createdByName: currentUser.displayName || profile?.displayName || 'Anonymous',
            actionUrl: '/emergency'
          });
        }
      } else {
        const docRef = await addDoc(collection(db, 'families', familyBoardId, 'emergencyInfo'), infoData);
        infoId = docRef.id;
        setSuccess('Emergency info added');

        // Send notifications to tagged members (only for new info)
        if (formData.taggedMembers && formData.taggedMembers.length > 0) {
          await notifyTaggedMembers(familyBoardId, formData.taggedMembers, {
            type: 'emergency_info',
            id: infoId,
            title: formData.name,
            createdBy: currentUser.uid,
            createdByName: currentUser.displayName || profile?.displayName || 'Anonymous',
            actionUrl: '/emergency'
          });
        }
      }

      setDialogOpen(false);
      setEditingInfo(null);
      setFormData({
        category: 'emergency',
        name: '',
        phone: '',
        notes: '',
        location: null,
        privacy: { visibility: 'public', memberIds: [], groupTypes: [] },
        taggedMembers: []
      });
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      logger.error('Error saving emergency info:', error);
      setError('Failed to save emergency information');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this emergency information?')) return;

    try {
      await deleteDoc(doc(db, 'families', familyBoardId, 'emergencyInfo', id));
      setSuccess('Emergency info deleted');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      logger.error('Error deleting emergency info:', error);
      setError('Failed to delete emergency information');
    }
  };

  const openViewDialog = (info) => {
    setSelectedInfo(info);
    setViewDialogOpen(true);
  };

  const closeViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedInfo(null);
  };

  const openEditDialog = (info) => {
    setEditingInfo(info);
    // Support both old (address/lat/lng at top level) and new (location object) schemas
    const location = info.location || (info.lat ? {
      name: info.address || '',
      address: info.address || '',
      lat: info.lat,
      lng: info.lng,
    } : null);
    setFormData({
      category: info.category,
      name: info.name,
      phone: info.phone || '',
      notes: info.notes || '',
      location,
      privacy: info.privacy || { visibility: 'public', memberIds: [], groupTypes: [] },
      taggedMembers: info.taggedMembers || []
    });
    setDialogOpen(true);
  };

  const openCreateDialog = () => {
    setEditingInfo(null);
    setFormData({
      category: 'emergency',
      name: '',
      phone: '',
      notes: '',
      location: null,
      privacy: { visibility: 'public', memberIds: [], groupTypes: [] },
      taggedMembers: []
    });
    setDialogOpen(true);
  };

  const handleCopy = (text, label) => {
    navigator.clipboard.writeText(text);
    setSuccess(`${label} copied to clipboard`);
    setTimeout(() => setSuccess(null), 2000);
  };

  const handleOpenSOS = async () => {
    setSosDialogOpen(true);
    setSosRecipientsLoading(true);
    try {
      const contactIds = (profile?.emergencyContactIds || []).filter(id => id !== currentUser.uid);
      if (contactIds.length > 0) {
        const userDocs = await Promise.all(contactIds.map(uid => getDoc(doc(db, 'users', uid))));
        const names = userDocs
          .filter(d => d.exists())
          .map(d => {
            const data = d.data();
            return ((data.firstName || '') + ' ' + (data.lastName || '')).trim() || data.displayName || data.email || 'Unknown';
          });
        setSosRecipients(names);
      } else {
        // No saved contacts — will default to all family members (same logic as FAB button)
        const membersSnap = await getDocs(collection(db, 'families', familyBoardId, 'members'));
        const memberIds = membersSnap.docs.map(d => d.data().userId || d.id).filter(id => id !== currentUser.uid);
        if (memberIds.length > 0) {
          const memberDocs = await Promise.all(memberIds.map(uid => getDoc(doc(db, 'users', uid))));
          const names = memberDocs
            .filter(d => d.exists())
            .map(d => {
              const data = d.data();
              return ((data.firstName || '') + ' ' + (data.lastName || '')).trim() || data.displayName || data.email || 'Unknown';
            });
          setSosRecipients(names);
        } else {
          setSosRecipients([]);
        }
      }
    } catch (err) {
      logger.error('Error loading SOS recipients:', err);
    } finally {
      setSosRecipientsLoading(false);
    }
  };

  const handleSendSOS = async () => {
    setSosSending(true);
    try {
      await sendEmergencySOS({
        userId: currentUser.uid,
        displayName: profile?.displayName || currentUser.displayName || 'A family member',
        familyCode: familyBoardId,
        message: `${profile?.displayName || 'A family member'} has triggered an emergency SOS alert!`,
        emergencyContactIds: profile?.emergencyContactIds || [],
        emergencyContacts: profile?.emergencyContacts || [],
      });
      setSosSent(true);
      setTimeout(() => { setSosSent(false); setSosDialogOpen(false); setSosRecipients([]); }, 3000);
    } catch (err) {
      setError('Failed to send SOS alert: ' + err.message);
      setSosDialogOpen(false);
    } finally {
      setSosSending(false);
    }
  };

  const handleCall = (phone) => {
    // Validate phone format to prevent XSS
    if (!phone || typeof phone !== 'string') return;
    const phoneRegex = /^[\d\s\-()+ ]+$/;
    if (!phoneRegex.test(phone)) return;
    window.location.href = `tel:${encodeURIComponent(phone)}`;
  };

  const getCategoryDetails = (categoryValue) => {
    return categories.find(cat => cat.value === categoryValue) || categories[0];
  };

  // Helper function to check if user can view emergency info based on privacy settings
  const canViewEmergencyInfo = (info) => {
    // Info author can always see their own info
    if (info.authorId === currentUser?.uid) {
      return true;
    }

    // If no privacy settings or public, everyone can see
    if (!info.privacy || info.privacy.visibility === 'public') {
      return true;
    }

    // For private info, check if user is in the allowed list
    if (info.privacy.visibility === 'private') {
      // Check if user ID is in memberIds
      if (info.privacy.memberIds && info.privacy.memberIds.includes(currentUser?.uid)) {
        return true;
      }

      // Check if user belongs to any of the allowed groups
      if (info.privacy.groupTypes && info.privacy.groupTypes.length > 0 && profile?.groups) {
        const userGroups = profile.groups || [];
        const hasGroupAccess = info.privacy.groupTypes.some(groupType =>
          userGroups.includes(groupType)
        );
        if (hasGroupAccess) {
          return true;
        }
      }

      // User doesn't have access to this private info
      return false;
    }

    return true;
  };

  // Filter emergency info based on privacy settings
  const visibleEmergencyInfo = emergencyInfo.filter(info => canViewEmergencyInfo(info));

  // Group by category
  const infoByCategory = visibleEmergencyInfo.reduce((acc, info) => {
    if (!acc[info.category]) {
      acc[info.category] = [];
    }
    acc[info.category].push(info);
    return acc;
  }, {});

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header with urgent styling */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          p: 3,
          borderRadius: 2,
          background: 'linear-gradient(135deg, #c62828 0%, #e53935 100%)',
          color: 'white',
          boxShadow: '0 4px 12px rgba(198, 40, 40, 0.3)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
            <EmergencyIcon sx={{ fontSize: 32 }} />
          </Avatar>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
              Emergency Information
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Quick access to critical contacts and information
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', border: '2px solid white', fontWeight: 700,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}
            startIcon={<SOSIcon />}
            onClick={handleOpenSOS}
          >
            Send SOS Alert
          </Button>
          <Button
            variant="contained"
            sx={{ bgcolor: 'white', color: 'error.main', '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' } }}
            startIcon={<AddIcon />}
            onClick={openCreateDialog}
          >
            Add Info
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {visibleEmergencyInfo.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center', bgcolor: 'background.default' }}>
          <WarningIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No emergency information added
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add important contacts and information for emergency situations
          </Typography>
          <Button variant="contained" color="error" startIcon={<AddIcon />} onClick={openCreateDialog}>
            Add First Entry
          </Button>
        </Paper>
      ) : (
        <Box>
          {categories.map((category) => {
            const categoryItems = infoByCategory[category.value] || [];
            if (categoryItems.length === 0) return null;

            return (
              <Box key={category.value} sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Avatar sx={{ bgcolor: category.color, width: 40, height: 40 }}>
                    {React.cloneElement(category.icon, { fontSize: 'small' })}
                  </Avatar>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: category.color }}>
                    {category.label}s
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  {categoryItems.map((info) => (
                    <Grid item xs={12} sm={6} md={4} key={info.id}>
                      <Card
                        sx={{
                          height: '100%',
                          border: '2px solid',
                          borderColor: category.color,
                          '&:hover': { boxShadow: 8, transform: 'translateY(-4px)' },
                          transition: 'all 0.3s',
                          cursor: 'pointer'
                        }}
                        onClick={() => openViewDialog(info)}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Chip
                              label={category.label}
                              size="small"
                              sx={{
                                bgcolor: category.color,
                                color: 'white',
                                fontWeight: 600
                              }}
                            />
                            {info.authorId === currentUser.uid && (
                              <Box>
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEditDialog(info); }}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDelete(info.id); }}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            )}
                          </Box>

                          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: category.color }}>
                            {info.name}
                          </Typography>

                          {info.phone && (
                            <Box sx={{ mb: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <PhoneIcon sx={{ color: 'text.secondary' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                  {info.phone}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="success"
                                  startIcon={<PhoneIcon />}
                                  onClick={(e) => { e.stopPropagation(); handleCall(info.phone); }}
                                  fullWidth
                                >
                                  Call
                                </Button>
                                <IconButton
                                  size="small"
                                  onClick={(e) => { e.stopPropagation(); handleCopy(info.phone, 'Phone number'); }}
                                  sx={{ border: '1px solid', borderColor: 'divider' }}
                                >
                                  <CopyIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>
                          )}

                          {(info.location || info.address) && (() => {
                            const loc = info.location || { name: info.address, address: info.address, lat: info.lat, lng: info.lng };
                            const mapsUrl = buildMapsUrl(loc);
                            const dirsUrl = buildDirectionsUrl(loc);
                            const displayAddr = loc.address || loc.name || '';
                            return (
                              <Box sx={{ mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                                  <LocationIcon sx={{ color: 'text.secondary', mt: 0.5 }} />
                                  <Box>
                                    {loc.name && loc.name !== loc.address && (
                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{loc.name}</Typography>
                                    )}
                                    {displayAddr && (
                                      <Typography variant="body2" color="text.secondary">{displayAddr}</Typography>
                                    )}
                                  </Box>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 0.75 }}>
                                  {displayAddr && (
                                    <Button size="small" variant="outlined" startIcon={<CopyIcon />}
                                      onClick={(e) => { e.stopPropagation(); handleCopy(displayAddr, 'Address'); }} sx={{ flex: 1 }}>
                                      Copy
                                    </Button>
                                  )}
                                  {mapsUrl && (
                                    <Button size="small" variant="outlined" color="primary" startIcon={<MapIcon />}
                                      component="a" href={mapsUrl} target="_blank" rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()} sx={{ flex: 1 }}>
                                      View
                                    </Button>
                                  )}
                                  {dirsUrl && (
                                    <Button size="small" variant="contained" color="primary" startIcon={<DirectionsIcon />}
                                      component="a" href={dirsUrl} target="_blank" rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()} sx={{ flex: 1 }}>
                                      Directions
                                    </Button>
                                  )}
                                </Box>
                              </Box>
                            );
                          })()}

                          {info.notes && (
                            <Box sx={{ mt: 2, p: 1.5, borderRadius: 1, bgcolor: 'background.default' }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                                Notes:
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {info.notes}
                              </Typography>
                            </Box>
                          )}

                          <Divider sx={{ my: 1.5 }} />

                          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            Added by {info.authorName}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Create/Edit Emergency Info Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingInfo ? 'Edit Emergency Info' : 'Add Emergency Info'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={formData.category}
                label="Category"
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {cat.icon}
                      <Typography>{cat.label}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Name/Title"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              placeholder="e.g., 911, Family Doctor, St. Mary's Hospital"
            />

            <TextField
              fullWidth
              label="Phone Number"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="e.g., 911, (555) 123-4567"
            />

            <LocationPicker
              label="Location / Address"
              placeholder="Search for a hospital, address, or place…"
              value={formData.location}
              onChange={(location) => setFormData({ ...formData, location })}
            />

            <TextField
              fullWidth
              label="Notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              multiline
              rows={3}
              placeholder="Additional information..."
            />

            <Divider sx={{ my: 2 }}>
              <Chip label="Privacy & Tagging" size="small" />
            </Divider>

            {/* Privacy Control */}
            <PrivacyControl
              familyCode={familyBoardId}
              value={formData.privacy}
              onChange={(privacy) => setFormData({ ...formData, privacy })}
              label="Who can see this emergency information?"
            />

            {/* Tag Members */}
            <TagMembers
              familyCode={familyBoardId}
              value={formData.taggedMembers}
              onChange={(taggedMembers) => setFormData({ ...formData, taggedMembers })}
              label="Tag Family Members"
              helperText="Tagged members will receive a notification about this emergency information"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="error">
            {editingInfo ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Emergency Info Dialog */}
      <Dialog open={viewDialogOpen} onClose={closeViewDialog} maxWidth="sm" fullWidth>
        {selectedInfo && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Avatar sx={{ bgcolor: getCategoryDetails(selectedInfo.category).color, width: 40, height: 40 }}>
                    {React.cloneElement(getCategoryDetails(selectedInfo.category).icon, { fontSize: 'small' })}
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {selectedInfo.name}
                  </Typography>
                </Box>
                <Chip
                  label={getCategoryDetails(selectedInfo.category).label}
                  size="small"
                  sx={{
                    bgcolor: getCategoryDetails(selectedInfo.category).color,
                    color: 'white',
                    fontWeight: 600
                  }}
                />
              </Box>
              <IconButton onClick={closeViewDialog}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {selectedInfo.phone && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PhoneIcon fontSize="small" /> Phone Number
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      {selectedInfo.phone}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<PhoneIcon />}
                        onClick={() => handleCall(selectedInfo.phone)}
                        fullWidth
                      >
                        Call Now
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<CopyIcon />}
                        onClick={() => handleCopy(selectedInfo.phone, 'Phone number')}
                      >
                        Copy
                      </Button>
                    </Box>
                  </Box>
                )}

                {(selectedInfo.location || selectedInfo.address) && (() => {
                  const loc = selectedInfo.location || { name: selectedInfo.address, address: selectedInfo.address, lat: selectedInfo.lat, lng: selectedInfo.lng };
                  const mapsUrl = buildMapsUrl(loc);
                  const dirsUrl = buildDirectionsUrl(loc);
                  const displayAddr = loc.address || loc.name || '';
                  return (
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationIcon fontSize="small" /> Location
                      </Typography>
                      {loc.name && loc.name !== loc.address && (
                        <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>{loc.name}</Typography>
                      )}
                      {displayAddr && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{displayAddr}</Typography>
                      )}
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {displayAddr && (
                          <Button size="small" variant="outlined" startIcon={<CopyIcon />}
                            onClick={() => handleCopy(displayAddr, 'Address')} sx={{ flex: 1 }}>
                            Copy
                          </Button>
                        )}
                        {mapsUrl && (
                          <Button size="small" variant="outlined" color="primary" startIcon={<MapIcon />}
                            component="a" href={mapsUrl} target="_blank" rel="noopener noreferrer" sx={{ flex: 1 }}>
                            View
                          </Button>
                        )}
                        {dirsUrl && (
                          <Button size="small" variant="contained" color="primary" startIcon={<DirectionsIcon />}
                            component="a" href={dirsUrl} target="_blank" rel="noopener noreferrer" sx={{ flex: 1 }}>
                            Directions
                          </Button>
                        )}
                      </Box>
                    </Box>
                  );
                })()}

                {selectedInfo.notes && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Additional Notes
                    </Typography>
                    <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                        {selectedInfo.notes}
                      </Typography>
                    </Paper>
                  </Box>
                )}

                <Divider />

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Added by {selectedInfo.authorName}
                  </Typography>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions>
              {selectedInfo.authorId === currentUser.uid && (
                <Button
                  onClick={() => {
                    closeViewDialog();
                    openEditDialog(selectedInfo);
                  }}
                  startIcon={<EditIcon />}
                >
                  Edit
                </Button>
              )}
              <Button onClick={closeViewDialog}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      {/* SOS Confirmation Dialog */}
      <Dialog open={sosDialogOpen} onClose={() => !sosSending && setSosDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <SOSIcon color="error" />
          Send Emergency SOS Alert
        </DialogTitle>
        <DialogContent>
          {sosSent ? (
            <Alert severity="success" sx={{ mb: 1 }}>
              SOS alert sent! {sosRecipients.length > 0 ? `Notified: ${sosRecipients.join(', ')}` : 'Family members notified.'}
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <Alert severity="warning">
                This will immediately notify your emergency contacts that you need help. Only use this in a genuine emergency.
              </Alert>
              {sosRecipientsLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={14} />
                  <Typography variant="caption" color="text.secondary">Loading contacts…</Typography>
                </Box>
              ) : sosRecipients.length > 0 ? (
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                    Will notify ({sosRecipients.length}):
                  </Typography>
                  {sosRecipients.map((name, i) => (
                    <Typography key={i} variant="body2">• {name}</Typography>
                  ))}
                </Box>
              ) : (
                <Alert severity="info">No emergency contacts configured. Set them up via the SOS button (red button).</Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {!sosSent && (
            <>
              <Button onClick={() => setSosDialogOpen(false)} disabled={sosSending}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={sosSending ? <CircularProgress size={16} color="inherit" /> : <SOSIcon />}
                onClick={handleSendSOS}
                disabled={sosSending}
                sx={{ fontWeight: 700 }}
              >
                {sosSending ? 'Sending...' : 'Send SOS Now'}
              </Button>
            </>
          )}
          {sosSent && (
            <Button onClick={() => setSosDialogOpen(false)} variant="contained">
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>

    </Container>
  );
};

export default Emergency;
